import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Users, Check, AlertTriangle } from "lucide-react";

/**
 * /invite/accept?token=... — landing page for teammates clicking an invite link.
 *
 * Flow:
 *   1. Fetch invite preview (public, read-only) → shows workspace + role.
 *   2. If the user is not signed in, prompt to sign in with the invited email.
 *   3. Once signed in, POST /invites/accept and redirect to the app.
 */
export default function InviteAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, loading: authLoading, refreshAuth } = useAuth();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This invite link is missing its token.");
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/invites/preview?token=${encodeURIComponent(token)}`);
        setPreview(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invite not found or already used.");
      }
    })();
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post("/invites/accept", { token });
      // Re-derive auth BEFORE navigating (Phase 5.2). This is a soft navigation:
      // without it the whole shell — workspace name, entitlements, membership
      // list — keeps rendering the state it held before the invite existed, and
      // nothing in the app ever corrects it short of a manual reload.
      //
      // COPY FLAG, not a code fix: `accept_invite` (backend/team.py) does NOT set
      // `active_workspace_id`. Accepting adds you to the workspace; it does not
      // move you into it. So this refresh makes the shell accurate — it does not
      // make the line below true. "Welcome to {workspace}!" then lands the user
      // on a dashboard for a DIFFERENT workspace, which reads as a bug even
      // though the membership was created correctly. Either the copy should say
      // what actually happened ("You're in — switch to {workspace} any time"),
      // or the product should decide accepting seats you there. That is a
      // product/voice call, so it is flagged rather than quietly changed here.
      await refreshAuth();
      toast.success(`Welcome to ${preview.workspace_name}!`);
      navigate("/app/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept invite.");
    } finally {
      setAccepting(false);
    }
  };

  const wrap = (children) => (
    <div className="min-h-screen bg-lm-tint/40 flex items-center justify-center px-4 py-10">
      <div
        className="max-w-md w-full bg-lm-surface border border-lm-border rounded-xl p-8"
        data-testid="invite-accept-card"
      >
        {children}
      </div>
    </div>
  );

  if (error) {
    return wrap(
      <div className="text-center">
        <div className="w-12 h-12 rounded-lg bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <h1 className="text-xl font-display font-semibold mb-2">Invite unavailable</h1>
        <p className="text-sm text-lm-text2 mb-5">{error}</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-lm-primary hover:underline"
        >
          Back to Leadmato
        </Link>
      </div>
    );
  }

  if (!preview || authLoading) {
    return wrap(
      <div className="flex items-center gap-2 text-lm-text2 text-sm py-4 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading invite…
      </div>
    );
  }

  const emailMismatch =
    user && user.email && user.email.toLowerCase() !== preview.invited_email.toLowerCase();

  return wrap(
    <div>
      <div className="w-12 h-12 rounded-lg bg-lm-tint2 flex items-center justify-center mx-auto mb-4">
        <Users className="w-5 h-5 text-lm-primary" />
      </div>
      <h1 className="text-xl font-display font-semibold mb-1 text-center">
        {preview.inviter_name ? `${preview.inviter_name} invited you` : "You’re invited"}
      </h1>
      <p className="text-sm text-lm-text2 mb-5 text-center">
        Join{" "}
        <span className="font-semibold text-lm-text">{preview.workspace_name}</span> on Leadmato
        as a <span className="font-mono uppercase text-xs">{preview.role}</span>.
      </p>

      <div className="rounded-lg bg-lm-tint/50 border border-lm-border p-4 text-xs font-mono text-lm-text2 mb-6">
        Invited email:{" "}
        <span className="text-lm-text" data-testid="invite-accept-email">
          {preview.invited_email}
        </span>
      </div>

      {!user ? (
        <div className="text-center">
          <p className="text-sm text-lm-text2 mb-4">
            Sign in with <span className="font-medium text-lm-text">{preview.invited_email}</span>{" "}
            to accept.
          </p>
          <Link
            to={`/login?redirect=${encodeURIComponent(`/invite/accept?token=${token}`)}`}
            className="inline-flex items-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-lm-primaryHover"
            data-testid="invite-accept-signin"
          >
            Sign in to accept
          </Link>
        </div>
      ) : emailMismatch ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 text-sm text-lm-text">
          You&rsquo;re signed in as <span className="font-mono">{user.email}</span>, but this
          invite is for <span className="font-mono">{preview.invited_email}</span>. Please sign
          out and sign back in with the correct address.
        </div>
      ) : (
        <button
          onClick={accept}
          disabled={accepting}
          data-testid="invite-accept-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-lm-primary text-white rounded-lg px-5 py-3 font-semibold hover:bg-lm-primaryHover disabled:opacity-60"
        >
          {accepting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          Accept &amp; join {preview.workspace_name}
        </button>
      )}
    </div>
  );
}
